import { useAuthContext } from './use-auth-context.hook';
import { AssistantInsights } from '../models/assistant-insights.model';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import { buildQueryParams } from '../utils/utils';
import dayjs from 'dayjs';

// Enhanced interfaces for better typing
export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface AnalyticsMetric {
  value: number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  period?: string;
}

export interface TimeSeriesData {
  date: string;
  value: number;
  label?: string;
}

export interface ProductAnalytics {
  productId: string;
  name: string;
  sku?: string;
  thumbnailPath: string;
  location: any;
  metrics: {
    totalViews: number;
    totalInteractions: number;
    totalDownloads: number;
    totalVisitProductPage: number;
    totalSearches: number;
    engagementRate: number;
    downloadRate: number;
    conversionRate: number;
    engagementScore: number;
    averageRating: number;
    averagePrice: number;
    uniqueUserCount: number;
  };
  trends: {
    viewsChange: number;
    interactionsChange: number;
    downloadsChange: number;
    engagementScoreChange: number;
    ratingChange: number;
  };
  performance: {
    rank: number;
    percentile: number;
    competitorComparison: string;
  };
}

export interface EngagementBreakdown {
  spaceSelection: number;
  productChange: number;
  uploads: number;
  search: number;
  rotation: number;
  compare: number;
  download: number;
}

export interface CityData {
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  uniqueUsers: number;
  events: number;
}

export interface CountryData {
  country: string;
  uniqueUsers: number;
  events: number;
}

export interface GeographicMetrics {
  topCities?: CityData[];
  topCountries?: CountryData[];
  continentDistribution?: {
    [continent: string]: number;
  };
}

export interface DashboardData {
  overview: {
    totalVisits: AnalyticsMetric;
    totalEngagements: AnalyticsMetric;
    activeProducts: AnalyticsMetric;
    conversionRate: AnalyticsMetric;
    avgSessionDuration: AnalyticsMetric;
  };
  trends: {
    visits: TimeSeriesData[];
    engagements: TimeSeriesData[];
    productUsage: TimeSeriesData[];
  };
  topProducts: ProductAnalytics[];
  engagementBreakdown: EngagementBreakdown;
  userJourney: {
    stages: Array<{
      stage: string;
      users: number;
      conversionRate: number;
    }>;
  };
  realTimeStats: {
    activeUsers: number;
    recentEvents: Array<{
      eventType: string;
      timestamp: string;
      userEmail?: string;
      productId?: string;
    }>;
  };
  geographicMetrics?: GeographicMetrics;
}

/** One named metric from GET /services/pms/metrics/query — see pms-api-v2 pms-metrics. */
export type MetricGroupBy = 'time' | 'product' | 'device' | 'surface' | 'origin' | 'eventType' | 'country' | 'city';

export interface MetricBreakdownRow {
  key: string;
  value: number;
}

export interface MetricResult {
  metric: string;
  label: string;
  unit: 'count' | 'percent' | 'rate';
  period: { from: string; to: string };
  /** Null when the metric is declared but has no data behind it yet. */
  value: number | null;
  previous?: number;
  change?: number | null;
  breakdown?: MetricBreakdownRow[];
  /** The previous window's time series, when grouped by time and compared. */
  previousBreakdown?: MetricBreakdownRow[];
  caveat?: string;
  /** Set when a zero probably is not a real zero (the event never fires). */
  warning?: string;
}

export interface MetricQueryParams {
  metric: string;
  /** YYYY-MM-DD, inclusive. */
  startDate: string;
  /** YYYY-MM-DD, inclusive of the whole day. */
  endDate: string;
  groupBy?: MetricGroupBy;
  granularity?: 'day' | 'week' | 'month';
  compareToPreviousPeriod?: boolean;
  limit?: number;
  productIds?: string[];
}

/** A search that returned nothing, grouped by term (ZERO_RESULT_SEARCH_TERMS). */
export interface ZeroResultSearchTerm {
  term: string;
  count: number;
  visitors: number;
  lastSearchedAt: string;
}

/** A default product of the catalogue, as the analytics pages need it — raw, not the editor's Product class. */
export interface CatalogProductRef {
  /** productsv2.id: the product every variant of it shares. */
  groupId: string;
  uuid: string;
  name: string;
  sku?: string;
  thumbnailPath?: string;
}

export interface AnalyticsFilters {
  dateRange: DateRange;
  productIds?: string[];
  eventTypes?: string[];
  userSegment?: string;
  comparisonPeriod?: DateRange;
}

export interface TimelineEvent {
  _id: string;
  dateTime: string;
  deviceType: string;
  visitorId: string;
  eventType: 'SpaceUploadDoneEvent' | 'ProductUsageEvent' | 'ProductVisitVendorPageEvent' | 'ProductResultDownloadedEvent' | 'EmailSentEvent' | 'EmailOpenedEvent' | 'EmailLinkClickedEvent';
  data: any;
  parsedDateTime: string;
  email?: string;
  product?: {
    id: string;
    name: string;
    thumbnailPath: string;
    link: string;
  };
}

export interface TimelineResponse {
  data: TimelineEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ExportOptions {
  format: 'csv' | 'pdf' | 'excel';
  includeCharts?: boolean;
  dateRange: DateRange;
  metrics?: string[];
}

export const useAnalyticsApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  // Get comprehensive dashboard data in a single optimized call
  const getDashboardData = async (filters: AnalyticsFilters): Promise<DashboardData> => {
    try {
      const queryParams = new URLSearchParams({
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate,
        ...(filters.productIds && { productIds: filters.productIds.join(',') }),
        ...(filters.eventTypes && { eventTypes: filters.eventTypes.join(',') }),
        ...(filters.userSegment && { userSegment: filters.userSegment }),
        ...(filters.comparisonPeriod && { 
          comparisonStartDate: filters.comparisonPeriod.startDate,
          comparisonEndDate: filters.comparisonPeriod.endDate 
        }),
      });

      const response: any = await sendAuthGuardedRequest(
        ApiMethod.GET,
        `${routes.analytics.dashboard}?${queryParams.toString()}`
      );
      // The API returns the journey as a bare array of stages; every reader
      // (the report, the export) expects { stages }.
      if (response && Array.isArray(response.userJourney)) {
        response.userJourney = { stages: response.userJourney };
      }
      return response as DashboardData;
    } catch (error) {
      // Fallback to legacy data aggregation until backend is updated
      console.warn('New dashboard endpoint not available, using legacy data');
      return await getLegacyDashboardData(filters);
    }
  };

  // Get specific metric with trend data
  const getMetricTrend = async (
    metricType: string, 
    filters: AnalyticsFilters,
    granularity: 'hour' | 'day' | 'week' | 'month' = 'day'
  ): Promise<TimeSeriesData[]> => {
    try {
      const queryParams = new URLSearchParams({
        metricType,
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate,
        granularity,
        ...(filters.productIds && { productIds: filters.productIds.join(',') }),
      });

      const response: any = await sendAuthGuardedRequest(
        ApiMethod.GET,
        `${routes.analytics.trends}?${queryParams.toString()}`
      );
      
      
      // Handle the wrapped response structure from backend
      if (response && response.success && response.data && response.data.trends) {
        
        const transformedData = response.data.trends.map((item: any) => ({
          date: item.timestamp,
          value: item.value,
          label: item.timestamp
        }));
        
        return transformedData;
      }
      
      // If response is already an array (legacy format)
      if (Array.isArray(response)) {
        return response.map((item: any) => ({
          date: item.date || item.timestamp || item._id,
          value: item.value || item.count || 0,
          label: item.label || item.date || item.timestamp || item._id
        }));
      }
      
      console.warn('Unexpected response format:', response);
      return [];
    } catch (error) {
      console.warn('Trends endpoint not available, using legacy data');
      return await getLegacyTrendData(metricType, filters);
    }
  };

  // Get detailed product analytics
  const getProductAnalytics = async (
    filters: AnalyticsFilters,
    limit: number = 10,
    sortBy?: 'engagement' | 'interactions' | 'views' | 'downloads' | 'rating'
  ): Promise<any> => {
    try {
      const queryParams = new URLSearchParams({
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate,
        limit: limit.toString(),
        ...(sortBy && { sortBy }),
        ...(filters.productIds && { productIds: filters.productIds.join(',') }),
      });

      const response = await sendAuthGuardedRequest(
        ApiMethod.GET,
        `${routes.analytics.products}?${queryParams.toString()}`
      );
      return response; // Return the full response for the calling code to handle
    } catch (error) {
      console.warn('Product analytics endpoint not available');
      return [];
    }
  };

  // Export analytics data
  const exportData = async (options: ExportOptions): Promise<Blob> => {
    const queryParams = new URLSearchParams({
      format: options.format,
      startDate: options.dateRange.startDate,
      endDate: options.dateRange.endDate,
      includeCharts: options.includeCharts?.toString() || 'false',
      ...(options.metrics && { metrics: options.metrics.join(',') }),
    });

    const response = await sendAuthGuardedRequest(
      ApiMethod.GET,
      `${routes.analytics.export}?${queryParams.toString()}`
    );

    return response as Blob;
  };

  // Legacy data aggregation functions
  const getLegacyDashboardData = async (filters: AnalyticsFilters): Promise<DashboardData> => {
    const [visitsData, engagementsData, vendorStats, topProductsData] = await Promise.all([
      queryAnalytics({
        statType: "VISITS_COUNT",
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate
      }),
      queryAnalytics({
        statType: "EVENT_USAGE_COUNT", 
        startDate: filters.dateRange.startDate,
        endDate: filters.dateRange.endDate
      }),
      findAllAnalytics(),
      getProductAnalytics(filters, 10).catch(() => null) // Fetch top products, fallback to null if error
    ]);

    // Aggregate data into new format
    const totalVisits = Array.isArray(visitsData) ? visitsData.reduce((sum, item) => sum + (item.count || 0), 0) : 0;
    const totalEngagements = Array.isArray(engagementsData) ? engagementsData.reduce((sum, item) => sum + (item.count || 0), 0) : 0;

    // Process top products data
    let topProducts: ProductAnalytics[] = [];
    if (topProductsData && topProductsData.success && topProductsData.data && Array.isArray(topProductsData.data.products)) {
      topProducts = topProductsData.data.products.map((product: any) => ({
        productId: product.productId || product._id || '',
        name: product.name || 'Unknown Product',
        sku: product.sku || '',
        thumbnailPath: product.thumbnailPath || '',
        location: product.location || {},
        metrics: {
          totalViews: product.metrics?.totalViews || 0,
          totalInteractions: product.metrics?.totalInteractions || 0,
          totalDownloads: product.metrics?.totalDownloads || 0,
          totalVisitProductPage: product.metrics?.totalVisitProductPage || 0,
          totalSearches: product.metrics?.totalSearches || 0,
          engagementRate: product.metrics?.engagementRate || 0,
          downloadRate: product.metrics?.downloadRate || 0,
          conversionRate: product.metrics?.conversionRate || 0,
          engagementScore: product.metrics?.engagementScore || 0,
          averageRating: product.metrics?.averageRating || 0,
          averagePrice: product.metrics?.averagePrice || 0,
          uniqueUserCount: product.metrics?.uniqueUserCount || 0,
        },
        trends: {
          viewsChange: product.trends?.viewsChange || 0,
          interactionsChange: product.trends?.interactionsChange || 0,
          downloadsChange: product.trends?.downloadsChange || 0,
          engagementScoreChange: product.trends?.engagementScoreChange || 0,
          ratingChange: product.trends?.ratingChange || 0,
        },
        performance: {
          rank: product.performance?.rank || 1,
          percentile: product.performance?.percentile || 0,
          competitorComparison: product.performance?.competitorComparison || 'Average performance',
        },
      }));
    }

    return {
      overview: {
        totalVisits: { value: totalVisits, change: 0, changeType: 'neutral' },
        totalEngagements: { value: totalEngagements, change: 0, changeType: 'neutral' },
        activeProducts: { value: vendorStats?.products?.activeProducts || 0, change: 0, changeType: 'neutral' },
        conversionRate: { value: totalVisits > 0 ? (totalEngagements / totalVisits) * 100 : 0, change: 0, changeType: 'neutral' },
        avgSessionDuration: { value: 0, change: 0, changeType: 'neutral' }
      },
      trends: {
        visits: Array.isArray(visitsData) ? visitsData.map(item => ({
          date: item._id,
          value: item.count || 0
        })) : [],
        engagements: Array.isArray(engagementsData) ? engagementsData.map(item => ({
          date: item._id,
          value: item.count || 0
        })) : [],
        productUsage: []
      },
      topProducts,
      engagementBreakdown: {
        spaceSelection: 0,
        productChange: 0,
        uploads: 0,
        search: 0,
        rotation: 0,
        compare: 0,
        download: 0
      },
      userJourney: { stages: [] },
      realTimeStats: { activeUsers: 0, recentEvents: [] }
    };
  };

  const getLegacyTrendData = async (metricType: string, filters: AnalyticsFilters): Promise<TimeSeriesData[]> => {
    let statType = "VISITS_COUNT";
    if (metricType.includes("engagement")) statType = "EVENT_USAGE_COUNT";
    if (metricType.includes("product")) statType = "PRODUCT_USAGE_COUNT";

    const data = await queryAnalytics({
      statType,
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate
    });

    return Array.isArray(data) ? data.map(item => ({
      date: item._id,
      value: item.count || 0
    })) : [];
  };

  // Legacy methods for backward compatibility
  const findAllAnalytics = async (
    limit?: number,
    offset?: number,
  ): Promise<any> => {
    const queryString = buildQueryParams([
      { key: "limit", value: limit?.toString() ?? "0" },
      { key: "offset", value: offset?.toString() ?? "600" },
    ]);

    const response: any = await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.analytics.findAll + queryString,
    );
    return response;
  };

  const queryAnalytics = async (query: any): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.POST, 
      routes.analytics.search,
      query
    );
  };

  const fetchTimelineEvents = async (params: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    email?: string;
    /** Narrows the timeline to these event types (the API ignores any it does not show). */
    eventTypes?: string[];
  } = {}): Promise<TimelineResponse> => {
    const queryParams = new URLSearchParams();
    if (params.eventTypes?.length) queryParams.append('eventTypes', params.eventTypes.join(','));
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.email) queryParams.append('email', params.email);

    const url = `${routes.analytics.timeline}?${queryParams.toString()}`;
    
    const response = await sendAuthGuardedRequest(
      ApiMethod.GET,
      url
    );
    return response as TimelineResponse;
  };

  /**
   * One metric from the registry. Resolves to null rather than throwing, so a
   * page built from several metrics still shows the ones that answered.
   */
  const queryMetric = async (params: MetricQueryParams): Promise<MetricResult | null> => {
    const query = new URLSearchParams({
      metric: params.metric,
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.groupBy) query.set('groupBy', params.groupBy);
    if (params.granularity) query.set('granularity', params.granularity);
    if (params.compareToPreviousPeriod) query.set('compareToPreviousPeriod', 'true');
    if (params.limit) query.set('limit', String(params.limit));
    if (params.productIds?.length) query.set('productIds', params.productIds.join(','));
    try {
      const response = await sendAuthGuardedRequest(ApiMethod.GET, `/services/pms/metrics/query?${query.toString()}`);
      return (response as MetricResult) ?? null;
    } catch (error) {
      console.warn(`Metric ${params.metric} failed`, error);
      return null;
    }
  };

  /**
   * What shoppers asked the visualizer's assistant for in the period: their
   * requests, the demand behind them, what nothing answered, the audience's
   * taste, estimates and quote requests. Null when the API does not have it.
   */
  const getAssistantInsights = async (startDate: string, endDate: string): Promise<AssistantInsights | null> => {
    try {
      const query = new URLSearchParams({ startDate, endDate });
      const response = await sendAuthGuardedRequest(ApiMethod.GET, `/services/pms/smart-assist/insights?${query.toString()}`);
      return (response as AssistantInsights) ?? null;
    } catch (error) {
      console.warn('Assistant insights failed', error);
      return null;
    }
  };

  /** Searches that found nothing in the period, most asked first. Dates are YYYY-MM-DD, inclusive. */
  const getZeroResultSearchTerms = async (startDate: string, endDate: string): Promise<ZeroResultSearchTerm[]> => {
    try {
      const rows = await queryAnalytics({
        statType: 'ZERO_RESULT_SEARCH_TERMS',
        startDate: `${startDate}T00:00:00.000Z`,
        endDate: `${endDate}T23:59:59.999Z`,
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  };

  /**
   * The visible, finished default products — one row per product, whatever its
   * variants — for "which products nobody tried". Capped at `limit`; `total`
   * says how many there are in all.
   */
  const getCatalogProducts = async (limit = 500): Promise<{ products: CatalogProductRef[]; total: number }> => {
    try {
      const query = new URLSearchParams({ isDefault: 'true', draft: 'exclude', isVisible: 'true', page: '1', limit: String(limit) });
      const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${routes.products.findAll}?${query.toString()}`);
      const rows: any[] = Array.isArray(response?.data) ? response.data : [];
      const text = (v: any) => (typeof v === 'string' ? v : v && typeof v === 'object' && typeof v.value === 'string' ? v.value : undefined);
      return {
        products: rows
          .filter((r) => r && (r.id || r.uuid))
          .map((r) => ({
            groupId: String(r.id ?? r.uuid),
            uuid: String(r.uuid ?? r.id),
            name: text(r.name) || text(r.sku) || '—',
            sku: text(r.sku),
            thumbnailPath: r.visualization?.thumbnailPath || r.visualization?.surface?.materialPath || undefined,
          })),
        total: Number(response?.total) || rows.length,
      };
    } catch {
      return { products: [], total: 0 };
    }
  };

  // Utility function to create date ranges
  const createDateRange = (days: number): DateRange => {
    const endDate = dayjs();
    const startDate = endDate.subtract(days, 'day');
    
    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
  };

  return { 
    // Modern API methods
    getDashboardData,
    getMetricTrend,
    getProductAnalytics,
    exportData,
    createDateRange,
    queryMetric,
    getZeroResultSearchTerms,
    getAssistantInsights,
    getCatalogProducts,
    
    // Legacy methods (for backward compatibility)
    findAllAnalytics,
    queryAnalytics,
    fetchTimelineEvents,
  };
};
  